import * as React from 'react';

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '../lib/utils';

import { buttonVariants } from './button';

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'vai-fixed vai-inset-0 vai-z-[9999] vai-bg-black/80 vai- data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'vai-fixed vai-left-[50%] vai-top-[50%] vai-z-[9999] vai-grid vai-w-full vai-max-w-lg vai-translate-x-[-50%] vai-translate-y-[-50%] vai-gap-4 vai-border vai-bg-background vai-p-6 vai-shadow-lg vai-duration-200 data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0 data-[state=closed]:vai-zoom-out-95 data-[state=open]:vai-zoom-in-95 data-[state=closed]:vai-slide-out-to-left-1/2 data-[state=closed]:vai-slide-out-to-top-[48%] data-[state=open]:vai-slide-in-from-left-1/2 data-[state=open]:vai-slide-in-from-top-[48%] sm:vai-rounded-lg',
        className,
      )}
      {...props}
    />
  </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'vai-font-inter vai-flex vai-flex-col vai-space-y-2 vai-text-center sm:vai-text-left',
      className,
    )}
    {...props}
  />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'vai-font-inter vai-flex vai-flex-col-reverse sm:vai-flex-row sm:vai-justify-end sm:vai-space-x-2',
      className,
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('vai-text-lg vai-font-semibold', className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('vai-text-sm vai-text-muted-foreground', className)}
    {...props}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> & {
    dataTestId?: string;
  }
>(({ className, dataTestId, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    data-testid={dataTestId}
    className={cn(buttonVariants({ variant: 'destructive' }), className)}
    {...props}
  />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> & {
    dataTestId?: string;
  }
>(({ className, dataTestId, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    data-testid={dataTestId}
    className={cn(
      buttonVariants({ variant: 'outline' }),
      'vai-mt-2 sm:vai-mt-0',
      className,
    )}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
